import { useState } from 'react';
import { Plus, Minus, Navigation, Compass, Layers, Map as MapIcon, Satellite, Mountain, Car, Ruler, RotateCcw } from 'lucide-react';
import { mapStyles } from '../../styles/mapTheme';

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onMyLocation: () => void;
  onMapTypeChange: (type: 'map' | 'satellite' | 'terrain') => void;
  onTrafficToggle: () => void;
  onMeasurementToggle: () => void;
  onCompassReset: () => void;
  mapType: 'map' | 'satellite' | 'terrain';
  showTraffic: boolean;
  measurementMode: boolean;
  compassHeading: number;
  isLocating?: boolean;
}

export default function MapControls({
  onZoomIn,
  onZoomOut,
  onMyLocation,
  onMapTypeChange,
  onTrafficToggle,
  onMeasurementToggle,
  onCompassReset,
  mapType,
  showTraffic,
  measurementMode,
  compassHeading,
  isLocating = false
}: MapControlsProps) {
  const [showLayerMenu, setShowLayerMenu] = useState(false);
  
  console.log('MapControls rendered with mapType:', mapType);

  const mapTypes = [
    { id: 'map' as const, name: 'Map', icon: MapIcon },
    { id: 'satellite' as const, name: 'Satellite', icon: Satellite },
    { id: 'terrain' as const, name: 'Terrain', icon: Mountain }
  ];

  return (
    <div className="space-y-2">
      {/* Zoom Controls */}
      <div className={`${mapStyles.control} overflow-hidden`}>
        <button
          onClick={onZoomIn}
          className={`${mapStyles.button} border-b border-gray-100 w-full`}
          title="Zoom in"
        >
          <Plus className="w-5 h-5 text-gray-600 mx-auto" />
        </button>
        <button
          onClick={onZoomOut}
          className={`${mapStyles.button} w-full`}
          title="Zoom out"
        >
          <Minus className="w-5 h-5 text-gray-600 mx-auto" />
        </button>
      </div>

      {/* Navigation Controls */}
      <button
        onClick={onMyLocation}
        className={`${mapStyles.control} ${mapStyles.button} ${isLocating ? 'opacity-75' : ''}`}
        title="My location"
        disabled={isLocating}
      >
        <Navigation className={`w-5 h-5 text-blue-600 ${isLocating ? 'animate-pulse' : ''}`} />
      </button>
      
      <button
        onClick={onCompassReset}
        className={`${mapStyles.control} ${mapStyles.button} relative`}
        title="Reset compass (North up)"
      >
        <Compass 
          className="w-5 h-5 text-gray-600 transition-transform duration-300" 
          style={{ transform: `rotate(${compassHeading}deg)` }}
        />
        {compassHeading !== 0 && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {/* Layer Controls */}
      <div className="relative">
        <button
          onClick={() => setShowLayerMenu(!showLayerMenu)}
          className={`${mapStyles.control} ${mapStyles.button}`}
          title="Map layers"
        >
          <Layers className="w-5 h-5 text-gray-600" />
        </button>

        {showLayerMenu && (
          <div className={`absolute bottom-full right-0 mb-2 ${mapStyles.card} min-w-40 z-50`}>
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">Map Type</div>
              {mapTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Map type button clicked:', type.id);
                      onMapTypeChange(type.id);
                      setShowLayerMenu(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded hover:bg-gray-50 transition-colors ${
                      mapType === type.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{type.name}</span>
                  </button>
                );
              })}
              
              <div className="border-t border-gray-100 mt-2 pt-2">
                <button
                  onClick={() => {
                    onTrafficToggle();
                    setShowLayerMenu(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded hover:bg-gray-50 transition-colors ${
                    showTraffic ? 'bg-green-50 text-green-700' : 'text-gray-700'
                  }`}
                >
                  <Car className="w-4 h-4" />
                  <span className="text-sm font-medium">Traffic</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Measurement Tool */}
      <button
        onClick={onMeasurementToggle}
        className={`${mapStyles.control} ${mapStyles.button} ${
          measurementMode ? 'bg-blue-50 border-blue-200' : ''
        }`}
        title="Measure distance"
      >
        <Ruler className={`w-5 h-5 ${measurementMode ? 'text-blue-600' : 'text-gray-600'}`} />
      </button>

      {/* Click outside to close layer menu */}
      {showLayerMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowLayerMenu(false)}
        />
      )}
    </div>
  );
}